import React, { Fragment, useState, useEffect, useCallback } from "react";
import { Row, Col, Card, CardBody, CardHeader, Button, Table } from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { ToastContainer, toast } from "react-toastify";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import AxiosInstance from "../../services/AxiosInstance";
import Spinner from "../../Components/Common/Spinner";
import NoDataFound from "../../Components/Common/NoDataFound";
import { customStyles } from "../../Constant";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";

const InvitationCampaignDetails = () => {
    const { campaignId } = useParams();
    const navigate = useNavigate();
    const [campaign, setCampaign] = useState(null);
    const [stats, setStats] = useState(null);
    const [recipients, setRecipients] = useState([]);
    const [totalRecipients, setTotalRecipients] = useState(0);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [sending, setSending] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 10;

    const fetchDetails = useCallback(async () => {
        if (!campaignId) return;
        setLoading(true);
        try {
            const res = await AxiosInstance().post(`/admin/invitation_campaign/details/${campaignId}`, { page: currentPage, per_page: perPage });
            if (res.data.status) {
                setCampaign(res.data.campaign);
                setStats(res.data.stats);
                setRecipients(Array.isArray(res.data.recipients) ? res.data.recipients : []);
                setTotalRecipients(res.data.total_recipients_list ?? 0);
            } else {
                toast.error(res.data.message || "Failed to load");
            }
        } catch (e) {
            toast.error(e.response?.data?.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, [campaignId, currentPage]);

    useEffect(() => {
        fetchDetails();
    }, [fetchDetails]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        try {
            const d = new Date(dateStr);
            return isNaN(d.getTime()) ? dateStr : d.toLocaleString();
        } catch {
            return dateStr;
        }
    };

    const handleGenerateRecipients = async () => {
        const result = await Swal.fire({
            title: "Generate recipients?",
            text: "This will add emails from recruiter_email_database (not unsubscribed) to this campaign.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Generate",
            cancelButtonText: "Cancel",
        });
        if (!result.isConfirmed) return;
        setGenerating(true);
        try {
            const res = await AxiosInstance().post(`/admin/invitation_campaign/generate-recipients/${campaignId}`);
            if (res.data.status) {
                toast.success(res.data.message || "Recipients generated");
                fetchDetails();
            } else {
                toast.error(res.data.message || "Failed");
            }
        } catch (e) {
            toast.error(e.response?.data?.message || "Failed");
        } finally {
            setGenerating(false);
        }
    };

    const handleSendCampaign = async () => {
        const result = await Swal.fire({
            title: "Send campaign?",
            text: "Emails will be sent to all PENDING recipients.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, send",
            cancelButtonText: "Cancel",
        });
        if (!result.isConfirmed) return;
        setSending(true);
        try {
            const res = await AxiosInstance().post(`/admin/invitation_campaign/send/${campaignId}`);
            if (res.data.status) {
                toast.success(res.data.message || "Campaign sent");
                fetchDetails();
            } else {
                toast.error(res.data.message || "Send failed");
            }
        } catch (e) {
            toast.error(e.response?.data?.message || "Send failed");
        } finally {
            setSending(false);
        }
    };

    const recipientColumns = [
        { name: "#", cell: (row, index) => (currentPage - 1) * perPage + index + 1, width: "50px" },
        { name: "Email", selector: (row) => row.invite_email || "—", grow: 1 },
        { name: "Status", selector: (row) => row.status || "—", width: "120px", minWidth: "120px", cell: (row) => <span className="badge bg-secondary" style={{ marginRight: "12px" }}>{row.status}</span> },
        { name: "Sent At", selector: (row) => row.sent_at, width: "155px", cell: (row) => formatDate(row.sent_at) },
        { name: "Opened At", selector: (row) => row.opened_at, width: "140px", cell: (row) => formatDate(row.opened_at) },
        { name: "Clicked At", selector: (row) => row.clicked_at, width: "140px", cell: (row) => formatDate(row.clicked_at) },
        { name: "Registered At", selector: (row) => row.registered_at, width: "140px", cell: (row) => formatDate(row.registered_at) },
    ];

    if (loading && !campaign) {
        return (
            <Fragment>
                <Breadcrumbs title="Campaign Details" mainTitle="Admin" />
                <div className="d-flex justify-content-center py-5"><Spinner /></div>
            </Fragment>
        );
    }

    if (!campaign) {
        return (
            <Fragment>
                <Breadcrumbs title="Campaign Details" mainTitle="Admin" />
                <Card><CardBody>Campaign not found.</CardBody></Card>
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Breadcrumbs title={campaign.campaign_name || "Campaign Details"} mainTitle="Admin" />
            <Row>
                <Col xs="12" md="6" lg="3">
                    <Card className="bg-primary text-white">
                        <CardBody>
                            <h6 className="text-white-50">Total Recipients</h6>
                            <h3>{stats?.total_recipients ?? 0}</h3>
                        </CardBody>
                    </Card>
                </Col>
                <Col xs="12" md="6" lg="3">
                    <Card className="bg-info text-white">
                        <CardBody>
                            <h6 className="text-white-50">Emails Sent</h6>
                            <h3>{stats?.emails_sent ?? 0}</h3>
                        </CardBody>
                    </Card>
                </Col>
                <Col xs="12" md="6" lg="3">
                    <Card className="bg-secondary text-white">
                        <CardBody>
                            <h6 className="text-white-50">Opened</h6>
                            <h3>{stats?.emails_opened ?? 0}</h3>
                        </CardBody>
                    </Card>
                </Col>
                <Col xs="12" md="6" lg="3">
                    <Card className="bg-success text-white">
                        <CardBody>
                            <h6 className="text-white-50">Clicked / Registered</h6>
                            <h3>{stats?.links_clicked ?? 0} / {stats?.registered ?? 0}</h3>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col xs="12">
                    <Card>
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <span>Recipients</span>
                            <div className="d-flex gap-2">
                                <Button color="secondary" size="sm" onClick={() => navigate("/job/admin/invitation-campaign")}>Back to List</Button>
                                <Button color="primary" size="sm" onClick={handleGenerateRecipients} disabled={generating}>{generating ? "Generating…" : "Generate Recipients"}</Button>
                                <Button color="success" size="sm" onClick={handleSendCampaign} disabled={sending}>{sending ? "Sending…" : "Send Campaign"}</Button>
                            </div>
                        </CardHeader>
                        <CardBody className="px-0">
                            <DataTable
                                responsive
                                customStyles={customStyles}
                                data={recipients}
                                columns={recipientColumns}
                                striped
                                pagination
                                paginationServer
                                paginationTotalRows={totalRecipients}
                                paginationPerPage={perPage}
                                onChangePage={setCurrentPage}
                                progressPending={loading}
                                progressComponent={<Spinner />}
                                noDataComponent={<NoDataFound message="No recipients" />}
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <ToastContainer />
        </Fragment>
    );
};

export default InvitationCampaignDetails;
